import { useEffect, useRef } from 'react';

/**
 * Reports `open` once for each id the calling surface shows.
 *
 * Deduped against a ref rather than left to the effect's dependency array. An effect can
 * run more than once for a single mount, and the whole subtree under `AppShell` used to
 * be torn down and rebuilt mid-load: `LaunchDarklyProvider` rendered `<>{children}</>`
 * until `asyncWithLDProvider` resolved and a provider element after, so the element type
 * at that slot changed and React deleted and remade everything below it. That put two
 * `invoice_opened` events a millisecond apart on every cold load. The asymmetry in the
 * browser traces was the giveaway: `usePageTracking` sits in `RootLayout` above the swap
 * and fired once; the invoice route sits below it and fired twice.
 *
 * The provider is mounted unconditionally now (AMP-741), so the subtree mounts once and
 * a per-mount ref is enough. The guard stays because these are funnel entries: a second
 * event per open inflates every rate keyed on one, and nothing about that slot's element
 * type is guaranteed to stay fixed.
 *
 * Keyed on the id, so reopening the same record after leaving it reports a second open
 * and switching between two records reports both.
 */
export function useTrackOncePerId(
  id: string | null | undefined,
  report: (id: string) => void,
): void {
  const lastReportedId = useRef<string | null>(null);
  const reportRef = useRef(report);

  useEffect(() => {
    reportRef.current = report;
  }, [report]);

  useEffect(() => {
    if (!id) {
      lastReportedId.current = null;
      return;
    }
    if (lastReportedId.current === id) return;
    lastReportedId.current = id;
    reportRef.current(id);
  }, [id]);
}
